<template>
  <div class="forms-list-wrapper">
    <TableNormal
      :value="value"
      @input="$emit('input', $event)"
      @click:row="(item) => $emit('review', item.id)"
      :headers="headers"
      :items="forms"
      :items-per-page="-1"
      :single-select="false"
      hide-default-footer
      item-key="id"
      :show-select="!isPhone"
      class="forms-list"
    >
      <!-- Phones: the count of the forms shown, in the count line the table draws above them. -->
      <template #phone-count>
        <span class="number-monospace">{{ forms.length }}</span>
        {{ ' ' + $tc('page.opportunity-forms.count', forms.length) }}
      </template>
      <template v-slot:header.data-table-select>
        <FormCheckbox
          class="forms-list__select-all"
          :indeterminate="partiallySelected"
          :afterIndeterminate="false"
          :onChange="onSelectAllClick"
          :value="allAreSelected"
        />
        <FormsActionsManager
          v-if="hasSelection"
          :forms="value"
          :templates="templates"
          :default-template-id="defaultTemplateId"
          :disabled="anyFilling"
          @fill="(templateId) => $emit('fill-selected', templateId)"
          @download="$emit('download-selected')"
          @delete="$emit('delete-selected')"
        />
      </template>
      <template v-slot:item.data-table-select="{ isSelected, select }">
        <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
      </template>

      <template v-slot:item.name="{ item }">
        <div class="forms-list__name">
          <span>{{ item.name }}</span>
          <!-- hover/focus shows the profile template the form was filled with -->
          <BaseIcon
            v-if="templateName(item) && !isPhone"
            name="file-text"
            class="forms-list__name-icon"
            tabindex="0"
            role="img"
            :aria-label="$t('page.opportunity-forms.filled-with', { template: templateName(item) })"
            v-tooltip="{
              content: $t('page.opportunity-forms.filled-with', { template: templateName(item) }),
              html: false,
            }"
          />
        </div>
      </template>

      <!-- Phones: no hover to show it, so the line under the form's name says it. -->
      <template v-slot:item.template="{ item }">
        <span v-if="templateName(item)">
          {{ $t('page.opportunity-forms.filled-with', { template: templateName(item) }) }}
        </span>
      </template>

      <template v-slot:item.status="{ item }">
        <BaseTag :tone="statusTone(item.status)" preset="soft">
          {{ $t(`page.opportunity-forms.status.${item.status.toLowerCase()}`) }}
        </BaseTag>
      </template>

      <template v-slot:item.progress="{ item }">
        <BaseTag preset="soft" :tone="isAllFilled(item.id) ? 'success' : 'warning'" class="forms-list__count">{{
          countLabel(item.id)
        }}</BaseTag>
      </template>

      <template v-slot:item.updatedAt="{ item }">
        <span class="forms-list__updated">{{ formatStandardDate(item.updatedAt) }}</span>
      </template>

      <!-- Phones: the row's next step, filling it, alone under the rest (a tap on the row reviews it). -->
      <template v-slot:item.fill="{ item }">
        <FillSplitButton
          is-small
          :label="$t('page.opportunity-forms.fill')"
          :templates="templates"
          :default-template-id="defaultTemplateId"
          :disabled="isFilling(item.id)"
          @click.native.stop
          @fill="(templateId) => $emit('fill', { formId: item.id, templateId })"
        />
      </template>

      <template v-slot:item.actions="{ item }">
        <!-- Phones: the others in the row's ⋯; it selects no rows, so a form is deleted from there. -->
        <BaseMoreActions
          v-if="isPhone"
          :title="item.name"
          :actions="rowActions(item)"
          @select="onRowAction($event, item)"
        />
        <div v-else class="forms-list__actions">
          <BaseButton
            v-if="canDownload(item)"
            is-small
            is-outlined
            has-icon-left
            @click.native.stop="$emit('download', item.id)"
          >
            <BaseIcon name="download" />
            {{ $t('page.opportunity-forms.download') }}
          </BaseButton>
          <BaseButton is-small is-outlined @click.native.stop="$emit('review', item.id)">
            {{ $t('page.opportunity-forms.review') }}
          </BaseButton>
          <FillSplitButton
            is-small
            :label="$t('page.opportunity-forms.fill')"
            :templates="templates"
            :default-template-id="defaultTemplateId"
            :disabled="isFilling(item.id)"
            @click.native.stop
            @fill="(templateId) => $emit('fill', { formId: item.id, templateId })"
          />
        </div>
      </template>
    </TableNormal>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import FillSplitButton from '@/components/page-opportunity-forms/FillSplitButton.vue';
import FormsActionsManager from '@/components/page-opportunity-forms/FormsActionsManager.vue';

import { mapGetters } from 'vuex';
import { formatStandardDate } from '@/util/date-util';
import { viewport } from '@/util/viewport';

const STATUS_TONE = {
  not_filled: 'neutral',
  filling: 'info',
  filled: 'success',
  partially_filled: 'warning',
  failed: 'destructive',
};

export default {
  name: 'FormsList',
  components: {
    BaseButton,
    BaseIcon,
    BaseMoreActions,
    BaseTag,
    FormCheckbox,
    TableNormal,
    FillSplitButton,
    FormsActionsManager,
  },
  props: {
    forms: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
    defaultTemplateId: {
      type: String,
      default: null,
    },
    anyFilling: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('formFill', ['isFilling', 'filledCountFor']),
    isPhone() {
      return viewport.size === 'phone';
    },
    hasSelection() {
      return this.value.length > 0;
    },
    allAreSelected() {
      return this.forms.length > 0 && this.value.length === this.forms.length;
    },
    partiallySelected() {
      return this.hasSelection && !this.allAreSelected;
    },
    headers() {
      return [
        { text: this.$t('page.opportunity-forms.table.name'), align: 'left', value: 'name', class: 'fixed-size-l' },
        ...(this.isPhone ? [{ text: '', value: 'template', phone: 'subtitle' }] : []),
        { text: this.$t('page.opportunity-forms.table.status'), align: 'left', value: 'status', phone: 'meta' },
        { text: this.$t('page.opportunity-forms.table.progress'), align: 'left', sortable: false, value: 'progress' },
        { text: this.$t('page.opportunity-forms.table.updated'), align: 'left', value: 'updatedAt' },
        ...(this.isPhone ? [{ text: '', sortable: false, value: 'fill', phone: 'block' }] : []),
        { text: '', align: 'right', sortable: false, value: 'actions' },
      ];
    },
  },
  watch: {
    // Rows are rebuilt on every opportunity refresh (fill progress, deletes, silent refetches), so
    // keep the selection by id, re-pointing it at the fresh row objects and dropping forms that
    // are no longer listed.
    forms(forms) {
      if (!this.value.length) return;
      const byId = new Map(forms.map((form) => [form.id, form]));
      const kept = this.value.filter((form) => byId.has(form.id)).map((form) => byId.get(form.id));
      const unchanged = kept.length === this.value.length && kept.every((form, i) => form === this.value[i]);
      if (!unchanged) this.$emit('input', kept);
    },
  },
  methods: {
    onSelectAllClick() {
      this.$emit('input', this.allAreSelected || this.partiallySelected ? [] : [...this.forms]);
    },
    statusTone(status) {
      return STATUS_TONE[(status || '').toLowerCase()] || 'neutral';
    },
    formatStandardDate,
    countLabel(formId) {
      const { filled, total } = this.filledCountFor(formId);
      return `${filled}/${total}`;
    },
    isAllFilled(formId) {
      const { filled, total } = this.filledCountFor(formId);
      return total > 0 && filled === total;
    },
    // The row's ⋯ on phones: what the desktop's other buttons do but review, which a tap on the
    // row does.
    rowActions(item) {
      return [
        ...(this.canDownload(item)
          ? [{ key: 'download', label: this.$t('page.opportunity-forms.download'), icon: 'download' }]
          : []),
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'download') this.$emit('download', item.id);
      else if (key === 'delete') this.$emit('delete-one', item);
    },
    canDownload(item) {
      return item.status === 'FILLED' || item.status === 'PARTIALLY_FILLED';
    },
    // Name of the profile template the form was filled with (`tenantProfileTemplateId`),
    // resolved against the tenant's templates; nothing (no icon) when the form was never
    // filled or the template has since been deleted.
    templateName(item) {
      if (!item.tenantProfileTemplateId) return '';
      const template = this.templates.find((t) => t.id === item.tenantProfileTemplateId);
      return template ? template.name : '';
    },
  },
};
</script>

<style lang="scss">
.forms-list-wrapper {
  width: 100%;
  overflow-x: auto;
}

.forms-list {
  // Anchor the absolutely-positioned actions manager to the select header cell,
  // and let it overflow across the header row (mirrors the tenders pattern).
  &.v-data-table thead th:first-child {
    position: relative;
    overflow: visible;
  }

  &__select-all {
    position: relative;
    z-index: 4;
    width: min-content;
  }

  &__name {
    display: flex;
    align-items: center;
    gap: $space-sm;
    @include text-style('body-md');
    color: $color-text-title;
  }

  &__name-icon {
    width: 18px;
    height: 18px;
    color: $color-text-muted;
    flex-shrink: 0;
    border-radius: 2px;

    &:focus-visible {
      outline: 2px solid $color-accent;
      outline-offset: 2px;
    }
  }

  &__count {
    font-variant-numeric: $font-variant-tabular;
  }

  &__updated {
    @include text-style('body-sm');
    color: $color-text-muted;
    font-variant-numeric: $font-variant-tabular;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $space-sm;
  }

  // Inherit the app-standard TableNormal styling; only set the name column width.
  &.v-data-table .v-data-table-header tr th.fixed-size-l {
    width: 30%;
  }
}
</style>
